import { useGrayScaleLogic } from './GrayScale.logic'
import './GrayScale.scss'
import { GrayScaleTrayMapProps } from './GrayScale.types'
import ConfirmScrewLoadModal from 'components/organisms/ConfirmScrewLoadModal/ConfirmScrewLoadModal'
import classNames from 'classnames'
import { SelectionBox } from './SelectionBox'
import ConfirmScrewSelectionsModal from 'components/organisms/ConfirmScrewSelectionsModal/ConfirmScrewSelectionsModal'
import BlockIcon from '@mui/icons-material/Block'
import Tooltip from '@mui/material/Tooltip'
import { BASE_CIRCLE_SIZES } from './constants'
import { OtherTrayItems } from 'components/organisms/OtherTrayItems'
import { CircularProgress } from '@mui/material'

const GrayScaleTrayMap = ({
  isInOR,
  setSelectedPlate,
  setModalOpen,
  trayType,
  setSelectedScrew,
  mapContent,
  isSPD = false,
  screwsRemovedByNurse,
  nurseUsageHistory,
  templateData,
}: GrayScaleTrayMapProps) => {
  const {
    screwAnnotations,
    plateAnnotations,
    drillAnnotations,
    selectedAnnotation,
    backgroundImage,
    originalWidth,
    originalHeight,
    containerRef,
    trayPlates,
    handleScrewClick,
    handleCountChange,
    handleCountCircleClick,
    shouldPlateOrDrillIncreaseBeDisabled,
    screwLoadModalOpen,
    handleCancelSelection,
    pendingScrewAsset,
    isDragging,
    startPoint,
    endPoint,
    selectedScrewIndices,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    isMultiSelectConfirmOpen,
    handleMultiSelectConfirm,
    handleMultiSelectCancel,
    isLoadingWithUDIs,
    trayScrews,
    usageScrews,
    textScale,
    finalScale,
    dynamicScale,
    isMobileOrTablet,
    imagePosition,
    getIsRemovedByNurse,
    isLoadingTemplate,
    templateError,
  } = useGrayScaleLogic({
    isInOR,
    setSelectedPlate,
    setModalOpen,
    trayType,
    setSelectedScrew,
    mapContent,
    isSPD,
    screwsRemovedByNurse,
    templateData,
  })

  if (isLoadingTemplate) {
    return (
      <div className="grayscale-tray-map">
        <div className="loading-container">
          <CircularProgress />
          <p style={{ marginTop: '16px', color: '#666' }}>
            Loading tray template...
          </p>
        </div>
      </div>
    )
  }

  if (templateError) {
    return (
      <div className="grayscale-tray-map">
        <div className="error-container">
          <p style={{ color: '#d32f2f', textAlign: 'center', margin: '16px' }}>
            Failed to load tray template: {templateError}
          </p>
          <p style={{ color: '#666', textAlign: 'center', fontSize: '14px' }}>
            Please try refreshing the page or contact support if the issue
            persists.
          </p>
        </div>
      </div>
    )
  }

  if (!backgroundImage) {
    return (
      <div className="grayscale-tray-map">
        <div className="loading-container">
          <p style={{ color: '#666', textAlign: 'center' }}>
            No tray template available
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
      <div
        data-testid="grayscale-tray-map"
        ref={containerRef}
        className="grayscale-tray-map annotation-container"
        onMouseDown={
          !isLoadingWithUDIs && !isInOR ? handleMouseDown : undefined
        }
        onMouseMove={
          !isLoadingWithUDIs && !isInOR ? handleMouseMove : undefined
        }
        onMouseUp={!isLoadingWithUDIs && !isInOR ? handleMouseUp : undefined}
        onDragStart={(e) => e.preventDefault()}
        style={{
          aspectRatio:
            originalWidth && originalHeight
              ? `${originalWidth}/${originalHeight}`
              : 'auto',
          cursor: isLoadingWithUDIs || isInOR ? 'default' : 'crosshair',
        }}
      >
        <img
          src={backgroundImage}
          key={backgroundImage}
          alt="Tray"
          className="gray-scale-tray-image"
          draggable={false}
          onDragStart={(e) => e.preventDefault()}
        />
        {/* Render Screw Annotations */}
        {screwAnnotations.map((annotation, index) => {
          const {
            img_screw_center,
            img_screw_radius,
            label,
            row_index,
            column_index,
          } = annotation

          if (!img_screw_center || img_screw_radius === undefined) return null

          const [cx, cy] = img_screw_center
          const r = img_screw_radius

          const scaledCX = cx * dynamicScale
          const scaledCY = cy * dynamicScale
          const scaledRadius = r * dynamicScale

          const isSelected = selectedScrewIndices.includes(index)
          const isRemovedByNurse = getIsRemovedByNurse(
            label,
            row_index,
            column_index
          )

          // Check if this screw was loaded via UDI scanning
          const loadedViUDIScrew = trayScrews.find(
            (screw) =>
              screw.label === label &&
              screw.row === row_index &&
              screw.x === column_index &&
              screw.loadedViaUDI === true
          )
          const isLoadedViaUDI = Boolean(loadedViUDIScrew)

          const notLoadedFromSPD =
            !trayScrews.find(
              (screw) =>
                screw.label === label &&
                screw.row === row_index &&
                screw.x === column_index
            ) &&
            !usageScrews.find(
              (screw) =>
                screw.label === label &&
                screw.row === row_index &&
                screw.x === column_index
            ) &&
            !nurseUsageHistory?.screws.find(
              (screw) =>
                screw.label === label &&
                screw.row === row_index &&
                screw.x === column_index
            )

          const isWasted =
            usageScrews.find(
              (screw) =>
                screw.label === label &&
                screw.row === row_index &&
                screw.x === column_index
            )?.wasted ||
            nurseUsageHistory?.screws.find(
              (screw) =>
                screw.label === label &&
                screw.row === row_index &&
                screw.x === column_index
            )?.wasted
          const shouldBeDisabled =
            isInOR && (notLoadedFromSPD || isRemovedByNurse)
          const displayCount = annotation.count
          const shouldAllowClick =
            !shouldBeDisabled ||
            (isInOR && shouldBeDisabled && isRemovedByNurse)

          return (
            <div
              key={`screw-${index}`}
              className={classNames('annotation-box screw', {
                spd: !isInOR,
                selected:
                  isSelected &&
                  (isInOR
                    ? displayCount === 1 || isRemovedByNurse
                    : displayCount === 0),
                disabled: shouldBeDisabled,
                wasted: isWasted,
                'removed-by-nurse': isRemovedByNurse,
              })}
              style={{
                left: `${imagePosition.left + scaledCX - scaledRadius}px`,
                top: `${imagePosition.top + scaledCY - scaledRadius}px`,
                width: `${scaledRadius * 2}px`,
                height: `${scaledRadius * 2}px`,
              }}
            >
              {isInOR &&
                displayCount === 0 &&
                !isWasted &&
                !shouldBeDisabled && (
                  <Tooltip title="Implanted" arrow placement="top">
                    <BlockIcon
                      className="disabled-icon implanted-icon"
                      sx={{
                        fontSize: `${scaledRadius * 1.5}px`,
                      }}
                    />
                  </Tooltip>
                )}
              {isInOR && isWasted && !shouldBeDisabled && (
                <Tooltip title="Wasted" arrow placement="top">
                  <BlockIcon
                    className="disabled-icon wasted-icon"
                    sx={{
                      fontSize: `${scaledRadius * 1.5}px`,
                    }}
                  />
                </Tooltip>
              )}
              {shouldBeDisabled && (
                <Tooltip
                  title={
                    isRemovedByNurse
                      ? 'Documented as not present by RN'
                      : 'Not loaded from SPD'
                  }
                  arrow
                  placement="top"
                  onClick={
                    shouldAllowClick ? () => handleScrewClick(index) : undefined
                  }
                >
                  <BlockIcon
                    className="disabled-icon"
                    sx={{
                      fontSize: `${scaledRadius * 1.5}px`,
                    }}
                  />
                </Tooltip>
              )}
              <div className="annotation-controls">
                <span
                  className={classNames('screw-ring', {
                    active: displayCount > 0,
                    disabled: shouldBeDisabled,
                    'loaded-via-udi': isLoadedViaUDI,
                  })}
                  onClick={
                    shouldAllowClick ? () => handleScrewClick(index) : undefined
                  }
                  style={{
                    width: `${scaledRadius * 2}px`,
                    height: `${scaledRadius * 2}px`,
                    cursor: shouldAllowClick ? 'pointer' : 'not-allowed',
                  }}
                  data-count={displayCount}
                  data-testid="screw-ring"
                />
              </div>
            </div>
          )
        })}

        {/* Render Plate Annotations */}
        {plateAnnotations.map((annotation, index) => {
          const { label, img_UL_coords, img_LR_coords } = annotation

          // Calculate scaled coordinates
          const [x1, y1] = img_UL_coords
          const [x2, y2] = img_LR_coords
          const scaledX1 = x1 * dynamicScale
          const scaledY1 = y1 * dynamicScale
          const scaledX2 = x2 * dynamicScale
          const scaledY2 = y2 * dynamicScale
          const width = scaledX2 - scaledX1
          const height = scaledY2 - scaledY1

          // Get current count from trayPlates
          const currentPlate = trayPlates.find(
            (plate) => plate.plateName === label
          )
          const displayCount = currentPlate?.plateCount || 0
          const isPlateLoadedViaUDI = Boolean(currentPlate?.loadedViaUDI)

          const uniqueId = `plate-${index}`
          const isSelected = selectedAnnotation === uniqueId

          return (
            <div
              key={`plate-${index}`}
              className="annotation-box plate"
              style={{
                left: `${imagePosition.left + scaledX1}px`,
                top: `${imagePosition.top + scaledY1}px`,
                width: `${width}px`,
                height: `${height}px`,
              }}
            >
              <div
                className={classNames('annotation-controls', {
                  selected: isSelected,
                })}
              >
                {isSelected && (
                  <div
                    className="count-controls"
                    style={{
                      transform: `scale(${Math.min(
                        finalScale * 8,
                        !isMobileOrTablet ? 0.9 : 1.2
                      )})`,
                      transformOrigin: 'center',
                    }}
                  >
                    <button
                      className="count-button decrease"
                      onClick={() => handleCountChange('plate', index, -1)}
                      disabled={displayCount <= 0}
                      aria-label="Decrease count"
                    >
                      <span>−</span>
                    </button>
                    <span
                      className={classNames('count-display', {
                        active: displayCount > 0,
                        selected: isSelected,
                        'loaded-via-udi': isPlateLoadedViaUDI,
                      })}
                      onClick={() => handleCountCircleClick('plate', index)}
                      style={{
                        width: `${Math.min(
                          BASE_CIRCLE_SIZES.plate.size * dynamicScale * 0.8,
                          24
                        )}px`,
                        height: `${Math.min(
                          BASE_CIRCLE_SIZES.plate.size * dynamicScale * 0.8,
                          24
                        )}px`,
                        fontSize: `${Math.min(
                          BASE_CIRCLE_SIZES.plate.font * textScale * 0.8,
                          12
                        )}px`,
                      }}
                    >
                      {displayCount}
                    </span>
                    <button
                      className="count-button increase"
                      onClick={() => handleCountChange('plate', index, 1)}
                      disabled={shouldPlateOrDrillIncreaseBeDisabled(label)}
                      aria-label="Increase count"
                    >
                      <span>+</span>
                    </button>
                  </div>
                )}
                {!isSelected && (
                  <span
                    className={classNames('count-circle', {
                      active: displayCount > 0,
                      'loaded-via-udi': isPlateLoadedViaUDI,
                    })}
                    onClick={() => handleCountCircleClick('plate', index)}
                    style={{
                      width: `${Math.min(
                        BASE_CIRCLE_SIZES.plate.size * dynamicScale * 0.8,
                        24
                      )}px`,
                      height: `${Math.min(
                        BASE_CIRCLE_SIZES.plate.size * dynamicScale * 0.8,
                        24
                      )}px`,
                      fontSize: `${Math.min(
                        BASE_CIRCLE_SIZES.plate.font * textScale * 0.8,
                        12
                      )}px`,
                    }}
                  >
                    {displayCount}
                  </span>
                )}
              </div>
            </div>
          )
        })}

        {/* Render Drill Annotations */}
        {drillAnnotations.map((annotation, index) => {
          const { label, img_UL_coords, img_LR_coords } = annotation

          // Calculate scaled coordinates
          const [x1, y1] = img_UL_coords
          const [x2, y2] = img_LR_coords
          const scaledX1 = x1 * dynamicScale
          const scaledY1 = y1 * dynamicScale
          const scaledX2 = x2 * dynamicScale
          const scaledY2 = y2 * dynamicScale
          const width = scaledX2 - scaledX1
          const height = scaledY2 - scaledY1

          // Get current count from trayPlates
          const currentPlate = trayPlates.find(
            (plate) => plate.plateName === label
          )
          const displayCount = currentPlate?.plateCount || 0

          const uniqueId = `drill-${index}`
          const isSelected = selectedAnnotation === uniqueId

          return (
            <div
              key={`drill-${index}`}
              className="annotation-box drill"
              style={{
                left: `${imagePosition.left + scaledX1}px`,
                top: `${imagePosition.top + scaledY1}px`,
                width: `${width}px`,
                height: `${height}px`,
              }}
            >
              <div
                className={classNames('annotation-controls', {
                  selected: isSelected,
                })}
              >
                {isSelected && (
                  <div
                    className="count-controls"
                    style={{
                      transform: `scale(${Math.min(
                        finalScale * 8,
                        !isMobileOrTablet ? 0.9 : 1.2
                      )})`,
                      transformOrigin: 'center',
                    }}
                  >
                    <button
                      className="count-button decrease"
                      onClick={() => handleCountChange('drill', index, -1)}
                      disabled={displayCount <= 0}
                      aria-label="Decrease count"
                    >
                      <span>-</span>
                    </button>
                    <span
                      className={`count-display ${
                        displayCount > 0 ? 'active' : ''
                      } selected`}
                      onClick={() => handleCountCircleClick('drill', index)}
                      style={{
                        width: `${Math.min(
                          BASE_CIRCLE_SIZES.plate.size * dynamicScale * 0.8,
                          24
                        )}px`,
                        height: `${Math.min(
                          BASE_CIRCLE_SIZES.plate.size * dynamicScale * 0.8,
                          24
                        )}px`,
                        fontSize: `${Math.min(
                          BASE_CIRCLE_SIZES.plate.font * textScale * 0.8,
                          12
                        )}px`,
                      }}
                    >
                      {displayCount}
                    </span>
                    <button
                      className="count-button increase"
                      onClick={() => handleCountChange('drill', index, 1)}
                      disabled={shouldPlateOrDrillIncreaseBeDisabled(label)}
                      aria-label="Increase count"
                    >
                      <span>+</span>
                    </button>
                  </div>
                )}
                {!isSelected && (
                  <span
                    className={`count-circle ${
                      displayCount > 0 ? 'active' : ''
                    }`}
                    onClick={() => handleCountCircleClick('drill', index)}
                    style={{
                      width: `${Math.min(
                        BASE_CIRCLE_SIZES.plate.size * dynamicScale * 0.8,
                        24
                      )}px`,
                      height: `${Math.min(
                        BASE_CIRCLE_SIZES.plate.size * dynamicScale * 0.8,
                        24
                      )}px`,
                      fontSize: `${Math.min(
                        BASE_CIRCLE_SIZES.plate.font * textScale * 0.8,
                        12
                      )}px`,
                    }}
                  >
                    {displayCount}
                  </span>
                )}
              </div>
            </div>
          )
        })}

        {isDragging && startPoint && endPoint && (
          <SelectionBox startPoint={startPoint} endPoint={endPoint} />
        )}

        <ConfirmScrewLoadModal
          open={screwLoadModalOpen}
          onClose={handleCancelSelection}
          pendingScrewAsset={pendingScrewAsset}
        />

        <ConfirmScrewSelectionsModal
          open={isMultiSelectConfirmOpen}
          modalText={`Are you sure you want to fill ${selectedScrewIndices.length} screws?`}
          onClose={handleMultiSelectCancel}
          handleConfirm={handleMultiSelectConfirm}
          handleSaveChangesCancellation={handleMultiSelectCancel}
        />
      </div>
      <OtherTrayItems trayType={trayType || ''} />
    </>
  )
}

export default GrayScaleTrayMap
